import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Search, Filter, ChevronLeft, Plane, ExternalLink,
  Info, BadgeCheck, ShieldAlert, X, ArrowLeft,
} from "lucide-react";
import { efFlightLogsStats, efFlightLogsList, efFlightLogDetail, efAircraftList } from "../lib/api";

/**
 * EpsteinFlightLogs — public landing page for the Flight Logs evidence lane.
 *
 * Route: /epstein-files/flight-logs (and /epstein-files/flight-logs/:id for
 * deep-link to a single record opening the detail panel).
 *
 * UX: research-database case-binder. Header + how-to + disclaimer + stats
 * + search + filter chips + table. Click a row to open the detail panel.
 * No graph view, no clutter. Reuses the Archive aesthetic.
 */

const VERIFY_META = {
  primary_log:           { label: "PRIMARY LOG",           color: "#5DD4B3", icon: BadgeCheck },
  court_exhibit:         { label: "COURT EXHIBIT",         color: "#9FA8D4", icon: BadgeCheck },
  secondary_compilation: { label: "SECONDARY COMPILATION", color: "#E0A526", icon: ShieldAlert },
  needs_review:          { label: "NEEDS REVIEW",          color: "#A39E93", icon: ShieldAlert },
};

const VerifyBadge = ({ status }) => {
  const m = VERIFY_META[status] || VERIFY_META.needs_review;
  const Icon = m.icon;
  return (
    <span
      title={`Verification: ${m.label}`}
      style={{ color: m.color, borderColor: `${m.color}55` }}
      className="font-mono text-[8.5px] uppercase tracking-[0.25em] border px-1.5 py-0.5 inline-flex items-center gap-1"
      data-testid={`ef-fl-verify-${status}`}
    >
      <Icon size={9} /> {m.label}
    </span>
  );
};

const EpsteinFlightLogs = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const deepLinkId = searchParams.get("event");

  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [aircraft, setAircraft] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [tail, setTail] = useState("");
  const [passenger, setPassenger] = useState("");
  const [departure, setDeparture] = useState("");
  const [arrival, setArrival] = useState("");
  const [verify, setVerify] = useState("");
  const [showHelp, setShowHelp] = useState(false);
  const [openDetail, setOpenDetail] = useState(null); // event detail object
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, a, l] = await Promise.all([
        efFlightLogsStats(),
        efAircraftList(),
        efFlightLogsList({
          q: q || undefined,
          tail_number: tail || undefined,
          passenger: passenger || undefined,
          departure: departure || undefined,
          arrival: arrival || undefined,
          verification: verify || undefined,
        }),
      ]);
      setStats(s);
      setAircraft(a?.aircraft || []);
      setEvents(l?.events || []);
    } catch (e) {
      console.error("flight-logs load failed", e);
    } finally {
      setLoading(false);
    }
  }, [q, tail, passenger, departure, arrival, verify]);

  useEffect(() => { load(); }, [load]);

  const openEvent = useCallback(async (id) => {
    setDetailLoading(true);
    try {
      const d = await efFlightLogDetail(id);
      setOpenDetail(d);
    } catch (e) {
      console.error("detail load failed", e);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  // Deep-link: ?event=<id>
  useEffect(() => {
    if (deepLinkId) openEvent(deepLinkId);
  }, [deepLinkId, openEvent]);

  const clearFilters = () => {
    setQ(""); setTail(""); setPassenger("");
    setDeparture(""); setArrival(""); setVerify("");
  };
  const anyFilter = q || tail || passenger || departure || arrival || verify;

  return (
    <div className="min-h-screen bg-[#0a0907] text-[#E4DFD3]" data-testid="ef-flight-logs-page">
      {/* Back to Epstein Files */}
      <div className="border-b border-[#2D2A26] bg-[#0F0F0F] px-3 sm:px-6 py-3">
        <Link
          to="/epstein-files"
          className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39E93] hover:text-[#D92D20]"
          data-testid="ef-fl-back"
        >
          <ArrowLeft size={12} /> // BACK TO EPSTEIN FILES
        </Link>
      </div>

      <main className="max-w-7xl mx-auto px-3 sm:px-6 py-6 sm:py-10">
        {/* Header */}
        <div className="mb-5">
          <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#D92D20] mb-1">
            // EPSTEIN FILES · EVIDENCE LANE
          </div>
          <h1 className="font-display text-3xl sm:text-5xl text-white tracking-tight mb-2">Flight Logs</h1>
          <p className="text-sm sm:text-base text-[#A39E93] max-w-3xl">
            A searchable database of publicly documented flight-log appearances,
            aircraft movements, and related travel records connected to Jeffrey
            Epstein&apos;s known aircraft activity.
          </p>
        </div>

        {/* Editorial / disclaimer */}
        <div
          className="border border-[#E0A526]/40 bg-[#1a1408] px-3 py-2 mb-4 max-w-4xl"
          data-testid="ef-fl-disclaimer"
        >
          <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#E0A526] mb-1">
            // EDITORIAL / LEGAL NOTE
          </div>
          <p className="text-xs text-[#CBB892] leading-relaxed">
            Records here are categorized by source strength. Inclusion in a
            flight record does not by itself imply guilt, knowledge of crimes,
            or participation in criminal conduct — flight records are presented
            as evidence artifacts and travel associations from the public
            record. Some entries are currently sourced from secondary public
            compilations until stronger primary source material is attached.
          </p>
        </div>

        {/* How-to */}
        <div className="mb-5 max-w-4xl">
          <button
            onClick={() => setShowHelp((v) => !v)}
            className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39E93] hover:text-[#5DD4B3] border border-[#2D2A26] hover:border-[#5DD4B3]/40 px-2.5 py-1.5"
            data-testid="ef-fl-howto-toggle"
          >
            <Info size={11} /> {showHelp ? "Hide" : "How to use Flight Logs"}
          </button>
          {showHelp && (
            <div
              className="mt-2 border border-[#2D2A26] bg-[#0F0F0F] px-3 py-3 text-sm text-[#CBB892] leading-relaxed"
              data-testid="ef-fl-howto-panel"
            >
              <ul className="list-disc pl-5 space-y-2">
                <li>This section tracks publicly documented flight-log appearances, aircraft movements, and related travel records connected to Jeffrey Epstein&apos;s known aircraft activity.</li>
                <li>Use the search bar or filters below to look up a passenger name, airport, aircraft tail number, or date range.</li>
                <li>Click any flight record to open the full entry, view source details, and see related people, places, and records tracked elsewhere in Epstein Files.</li>
                <li>A person appearing in a flight log does not by itself establish guilt, knowledge of crimes, or participation in criminal conduct.</li>
                <li>Some records may be based on secondary public compilations until stronger primary source material is attached. Verification badges make that clear at a glance.</li>
              </ul>
            </div>
          )}
        </div>

        {/* Stats strip */}
        {stats?.public_visible && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-5 max-w-4xl" data-testid="ef-fl-stats">
            <StatTile label="Flight Records"      value={stats.flight_events} />
            <StatTile label="Passenger Appearances" value={stats.passenger_appearances} />
            <StatTile label="Aircraft Tracked"    value={stats.aircraft_tracked} />
            <StatTile label="Linked Sources"      value={stats.linked_sources} />
          </div>
        )}

        {/* Search + filters */}
        <div className="border border-[#2D2A26] bg-[#0F0F0F] p-3 mb-3" data-testid="ef-fl-filters">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <Filter size={12} className="text-[#D92D20]" />
            <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#D92D20]">// SEARCH &amp; FILTERS</span>
            {anyFilter && (
              <button
                onClick={clearFilters}
                className="ml-auto inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.25em] text-[#A39E93] hover:text-[#D92D20]"
                data-testid="ef-fl-clear"
              >
                <X size={10} /> Clear
              </button>
            )}
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            <SearchField icon={<Search size={11} />} placeholder="Search records, routes, summaries…" value={q} onChange={setQ} testid="ef-fl-q" />
            <SelectField label="Aircraft" value={tail} onChange={setTail} testid="ef-fl-tail"
              options={[["", "All aircraft"], ...aircraft.map((a) => [a.tail_number, `${a.tail_number} · ${a.aircraft_model || ""}`])]} />
            <SearchField icon={<Search size={11} />} placeholder="Passenger name" value={passenger} onChange={setPassenger} testid="ef-fl-passenger" />
            <SearchField icon={<Plane size={11} />} placeholder="Departure (airport / city)" value={departure} onChange={setDeparture} testid="ef-fl-dep" />
            <SearchField icon={<Plane size={11} className="rotate-90" />} placeholder="Arrival (airport / city)" value={arrival} onChange={setArrival} testid="ef-fl-arr" />
            <SelectField label="Verification" value={verify} onChange={setVerify} testid="ef-fl-verify"
              options={[["",""+"All verifications"],["primary_log","Primary log"],["court_exhibit","Court exhibit"],["secondary_compilation","Secondary compilation"],["needs_review","Needs review"]]} />
          </div>
        </div>

        {/* Database table */}
        <div className="border border-[#2D2A26] bg-[#0F0F0F] overflow-hidden" data-testid="ef-fl-table">
          {loading ? (
            <div className="px-3 py-6 font-mono text-xs uppercase tracking-widest text-[#757065]">// LOADING FLIGHT RECORDS…</div>
          ) : events.length === 0 ? (
            <div className="px-3 py-6 font-mono text-xs uppercase tracking-widest text-[#757065]">// NO RECORDS MATCH THE CURRENT FILTERS</div>
          ) : (
            <>
              {/* Desktop header row */}
              <div className="hidden sm:grid grid-cols-[110px_120px_1fr_120px_140px_40px] gap-2 px-3 py-2 border-b border-[#2D2A26] font-mono text-[9px] uppercase tracking-[0.25em] text-[#757065] bg-[#0a0907]">
                <span>Date</span>
                <span>Aircraft</span>
                <span>Route</span>
                <span>Passengers</span>
                <span>Verification</span>
                <span></span>
              </div>
              {events.map((e) => (
                <button
                  key={e.id}
                  onClick={() => openEvent(e.id)}
                  data-testid={`ef-fl-row-${e.id}`}
                  className="w-full text-left grid grid-cols-1 sm:grid-cols-[110px_120px_1fr_120px_140px_40px] gap-2 px-3 py-2.5 border-b border-[#2D2A26]/70 hover:bg-[#161514] transition-colors"
                >
                  <span className="font-mono text-[11px] text-[#E4DFD3]">{e.event_date || "—"}</span>
                  <span className="font-mono text-[11px] text-[#CBB892]">{e.tail_number || "—"}<span className="text-[#757065] text-[10px]"> · {e.aircraft_model || ""}</span></span>
                  <span className="text-sm text-[#E4DFD3] truncate" title={e.route_label || e.name}>{e.route_label || e.name}</span>
                  <span className="font-mono text-[11px] text-[#A39E93]">{e.passenger_count ?? 0} listed</span>
                  <span><VerifyBadge status={e.verification_status} /></span>
                  <span className="text-right text-[#D92D20] font-mono text-[10px]">→</span>
                </button>
              ))}
            </>
          )}
        </div>

        <div className="mt-3 text-[10px] font-mono text-[#757065] uppercase tracking-[0.25em]">
          // Showing {events.length} record{events.length === 1 ? "" : "s"}
          {stats?.flight_events ? ` of ${stats.flight_events} public` : ""}
        </div>
      </main>

      {/* Detail panel — slide-in from right on desktop, full-screen on mobile */}
      {(openDetail || detailLoading) && (
        <DetailPanel
          detail={openDetail}
          loading={detailLoading}
          onClose={() => { setOpenDetail(null); if (deepLinkId) navigate("/epstein-files/flight-logs", { replace: true }); }}
        />
      )}
    </div>
  );
};

// -------- small UI helpers ----------------------------------------------

const StatTile = ({ label, value }) => (
  <div className="border border-[#2D2A26] bg-[#0F0F0F] px-3 py-2" data-testid={`ef-fl-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065]">{label}</div>
    <div className="font-heading text-2xl text-white mt-0.5">{Number.isFinite(value) ? value : "—"}</div>
  </div>
);

const SearchField = ({ icon, placeholder, value, onChange, testid }) => (
  <label className="block">
    <div className="flex items-center gap-2 border border-[#2D2A26] focus-within:border-[#D92D20] bg-[#0a0907] px-2.5 py-2">
      <span className="text-[#757065]">{icon}</span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="flex-1 bg-transparent outline-none font-mono text-[12px] text-[#E4DFD3] placeholder:text-[#5a544a]"
      />
    </div>
  </label>
);

const SelectField = ({ label, value, onChange, options, testid }) => (
  <label className="block">
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      aria-label={label}
      className="w-full border border-[#2D2A26] focus:border-[#D92D20] bg-[#0a0907] px-2.5 py-2 font-mono text-[12px] text-[#E4DFD3] outline-none"
    >
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  </label>
);

const DetailPanel = ({ detail, loading, onClose }) => {
  if (!detail && !loading) return null;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 flex sm:justify-end"
      onClick={onClose}
      data-testid="ef-fl-detail-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-[#0F0F0F] w-full sm:max-w-2xl h-full overflow-y-auto border-l border-[#2D2A26] flex flex-col"
        data-testid="ef-fl-detail-panel"
      >
        <div className="sticky top-0 bg-[#0F0F0F] border-b border-[#2D2A26] px-4 py-3 flex items-center justify-between z-10">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20]">// FLIGHT RECORD</div>
          <button onClick={onClose} className="text-[#A39E93] hover:text-[#D92D20]" data-testid="ef-fl-detail-close">
            <X size={16} />
          </button>
        </div>

        {loading || !detail ? (
          <div className="p-6 font-mono text-xs uppercase tracking-widest text-[#757065]">// LOADING…</div>
        ) : (
          <div className="p-4 space-y-5">
            {/* Flight summary */}
            <div>
              <h2 className="font-display text-2xl text-white mb-1">{detail.event?.name}</h2>
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <VerifyBadge status={detail.event?.verification_status} />
                <span className="font-mono text-[10px] text-[#A39E93]">{detail.event?.event_date}</span>
              </div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 font-mono text-[11px]">
                <span className="text-[#757065] uppercase tracking-[0.2em]">date</span>
                <span className="text-[#E4DFD3]">{detail.event?.event_date || "—"}</span>
                <span className="text-[#757065] uppercase tracking-[0.2em]">aircraft</span>
                <span className="text-[#E4DFD3]">{detail.event?.tail_number} · {detail.event?.aircraft_model}</span>
                <span className="text-[#757065] uppercase tracking-[0.2em]">departure</span>
                <span className="text-[#E4DFD3]">{detail.event?.departure_label || "—"}</span>
                <span className="text-[#757065] uppercase tracking-[0.2em]">arrival</span>
                <span className="text-[#E4DFD3]">{detail.event?.arrival_label || "—"}</span>
                {detail.event?.route_label && (
                  <>
                    <span className="text-[#757065] uppercase tracking-[0.2em]">route</span>
                    <span className="text-[#E4DFD3]">{detail.event.route_label}</span>
                  </>
                )}
              </div>
              {detail.event?.summary && (
                <p className="mt-3 text-sm text-[#A39E93] leading-relaxed">{detail.event.summary}</p>
              )}
              {detail.legal_note && (
                <div className="mt-3 border border-[#E0A526]/40 bg-[#1a1408] px-2.5 py-2 text-[11px] text-[#CBB892]">
                  <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#E0A526] mr-1.5">LEGAL NOTE:</span>{detail.legal_note}
                </div>
              )}
            </div>

            {/* Passengers */}
            <div data-testid="ef-fl-detail-passengers">
              <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#D92D20] mb-2">// PASSENGER APPEARANCES · {detail.passengers?.length || 0}</div>
              {(!detail.passengers || detail.passengers.length === 0) ? (
                <div className="text-xs text-[#757065]">No public passenger appearances recorded for this flight.</div>
              ) : (
                <div className="space-y-1.5">
                  {detail.passengers.map((p) => (
                    <div key={p.id} className="border border-[#2D2A26] bg-[#0a0907] px-2.5 py-2 flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        {p.linked_person ? (
                          <Link
                            to={`/epstein-files/entity/${p.linked_person.slug}`}
                            className="block font-heading text-sm text-[#E4DFD3] hover:text-[#D92D20] truncate"
                          >
                            {p.linked_person.name}
                          </Link>
                        ) : (
                          <div className="font-heading text-sm text-[#E4DFD3] truncate">{p.passenger_name_normalized || p.passenger_name_raw}</div>
                        )}
                        {p.passenger_name_raw && p.passenger_name_raw !== (p.linked_person?.name || p.passenger_name_normalized) && (
                          <div className="font-mono text-[10px] text-[#757065]">recorded as: “{p.passenger_name_raw}”</div>
                        )}
                        {p.notes && <div className="text-[11px] italic text-[#A39E93] mt-0.5">{p.notes}</div>}
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        <VerifyBadge status={p.verification_status} />
                        <span className={`font-mono text-[8.5px] uppercase tracking-[0.2em] ${p.match_status === "matched" ? "text-[#5DD4B3]" : p.match_status === "possible_match" ? "text-[#E0A526]" : "text-[#757065]"}`}>
                          {p.match_status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Aircraft cross-link */}
            {detail.aircraft && (
              <div data-testid="ef-fl-detail-aircraft">
                <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#D92D20] mb-2">// AIRCRAFT</div>
                <Link
                  to={`/epstein-files/entity/${detail.aircraft.slug}`}
                  className="block border border-[#2D2A26] bg-[#0a0907] px-2.5 py-2 hover:border-[#D92D20]/60"
                >
                  <div className="flex items-center gap-2">
                    <Plane size={14} className="text-[#E0A526]" />
                    <div className="min-w-0">
                      <div className="font-heading text-sm text-[#E4DFD3]">{detail.aircraft.name}</div>
                      <div className="font-mono text-[10px] text-[#A39E93]">{detail.aircraft.tail_number} · {detail.aircraft.aircraft_model}</div>
                    </div>
                  </div>
                </Link>
              </div>
            )}

            {/* Sources */}
            <div data-testid="ef-fl-detail-sources">
              <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#D92D20] mb-2">// SOURCES · {detail.sources?.length || 0}</div>
              {(!detail.sources || detail.sources.length === 0) ? (
                <div className="text-xs text-[#757065]">No sources currently attached to this record.</div>
              ) : (
                <div className="space-y-2">
                  {detail.sources.map((s) => (
                    <div key={s.id} className="border border-[#2D2A26] bg-[#0a0907] px-2.5 py-2">
                      <div className="flex flex-wrap items-center gap-1.5 mb-1">
                        <span className="font-mono text-[8.5px] uppercase tracking-[0.25em] text-[#5DD4B3] border border-[#5DD4B3]/40 px-1.5 py-0.5">
                          {(s.source_type || "source").replace(/_/g, " ")}
                        </span>
                        {s.source_origin && (
                          <span className="font-mono text-[8.5px] uppercase tracking-[0.25em] text-[#9FA8D4] border border-[#9FA8D4]/40 px-1.5 py-0.5">
                            {s.source_origin}
                          </span>
                        )}
                        {s.publisher && <span className="font-mono text-[9px] text-[#757065] truncate">{s.publisher}</span>}
                      </div>
                      <Link to={`/epstein-files/source/${s.id}`} className="block hover:text-[#D92D20]">
                        <div className="font-heading text-sm text-[#E4DFD3]">{s.title}</div>
                      </Link>
                      {s.excerpt && <div className="text-[11px] italic text-[#A39E93] mt-1 line-clamp-3">{s.excerpt}</div>}
                      {s.url && (
                        <a href={s.url} target="_blank" rel="noopener noreferrer"
                           className="inline-flex items-center gap-1 mt-1 font-mono text-[10px] text-[#A39E93] hover:text-[#D92D20] break-all">
                          {s.url} <ExternalLink size={9} />
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EpsteinFlightLogs;
